/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

var InfiniteScroll = class extends CustomHTMLElement {
	connectedCallback() {
    this.isCollectionEnd = false;
    this.productListInner = this.querySelector(".product-list__inner")
    this.totalProducts = this._getTotalProducts(this);
    this.progressBar = this.querySelector('.progress-bar__inner');
    this._calculateProgressBar();

		this.delegate.on(
			"click",
			"[data-paginate-btn]",
			this._loadMoreProducts.bind(this),
    );
    this.delegate.on('click', '.inf-scroll__back-to-top', this._scrollToTop.bind(this));

  }

  _scrollToTop(event) {
    const scrollOptions = {
      top: 0,
      left: 0,
      behavior: 'smooth'
    };

    const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style;
    event.preventDefault();

    // Scroll to top.
    supportsNativeSmoothScroll ? window.scrollTo(scrollOptions) : window.scrollTo(scrollOptions.left, scrollOptions.top);
    const focusableElements = document.querySelectorAll('button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])');

    // Focus the first focusable element.
    focusableElements[0].focus({
      preventScroll: true,
    });
  }

  _getTotalProducts(target) {
    return target.querySelector('.progress-bar').getAttribute('data-total-size');
  }

  _getTotalRenderedProducts() {
    return document.querySelectorAll('.product-item').length;
  }

  _replacePaginateBtn() {
    document.querySelector(".inf-scroll__label").innerText = "No more products";
    document.querySelector('.inf-scroll__back-to-top').classList.remove('visually-hidden');
    document.querySelector('[data-paginate-btn]').remove();
  }

  _loadMoreProducts(event) {
    this.currentPage++;

    if (!this.isCollectionEnd) {
      let url = document.querySelector(".pagination__next").href;
      const target = document.querySelector("product-list");

      event.target.setAttribute('aria-busy', true);
      const loadingSpinner = document.createElement('div');
      loadingSpinner.classList.add('spinner');
      loadingSpinner.innerHTML = `
          <svg focusable="false" width="24" height="24" class="icon icon--spinner" viewBox="25 25 50 50">
            <circle cx="50" cy="50" r="20" fill="none" stroke="currentColor" stroke-width="5"></circle>
          </svg>
      `;

      event.target.innerText = '';
      event.target.append(loadingSpinner);

      fetch(url)
				.then((res) => res.text())
        .then((html) => {

					const parser = new DOMParser();
					const doc = parser.parseFromString(html, "text/html");
          const newProducts = doc.querySelectorAll(".product-item");
					const newPagination = doc.querySelector("page-pagination");

					document.querySelector("page-pagination").replaceWith(newPagination);

          if (newProducts.length > 0) {
            document.querySelector(".product-list__inner").append(...newProducts);
            this._animateProductGrid(newProducts);

            //this.loading = false;
            event.target.innerHTML = 'Load more';

            this._calculateProgressBar();

						if (!document.querySelector(".pagination__next")) {
              this.isCollectionEnd = true;
              this._replacePaginateBtn(event);

            } else {
              event.target.setAttribute('aria-busy', false);
						}
					}
				})
				.catch((err) => console.error("Infinite scroll error:", err));
		}
  }

  _animateProductGrid(newProducts) {
    newProducts.forEach((item) => {
      item.style.opacity = 0;
      let op = 0;
      var timer = setInterval(function() {
          if (op == 1){
              clearInterval(timer);
              item.style.opacity = 'none';
          }
          item.style.opacity = op;
          op += 0.1;
      }, 30);
    });

  }

  _calculateProgressBar() {
    const progressBarRemaining = Math.ceil((this._getTotalRenderedProducts() / this.totalProducts) * 100);
    this._updateProgressBar(this.progressBar, progressBarRemaining);
  }

  _updateProgressBar(target, percentage) {
    target.style.setProperty('--progress', `${percentage}%`);
    if (percentage === 100) {
      this._replacePaginateBtn();
    }

    this._updateLabel();
  }

  _updateLabel() {
    document.querySelector(".inf-scroll__label").innerText = `${this._getTotalRenderedProducts()} of ${this.totalProducts} of products viewed`;
  }

};
window.customElements.define("infinite-scroll", InfiniteScroll);

class DeliveryInfo extends CustomHTMLElement {
  connectedCallback() {
    this.deliveryCutoff = 16;
    this.deliveryDays = [[1],[2],[3],[4],[5]];
    this.nextDelivery = this._calculateNextDeliveryDay();
    this._calculateRemainingTime();
  }
  
  _calculateRemainingTime() {
    const now = new Date();
    this._renderDeliveryTime();
    
    setTimeout(() => {
      this.interval = setInterval(() => {
        const delivery = this.nextDelivery.nextDeliveryDay;
        const diff = (this.nextDelivery.nextDispatchDay - Date.now());

        if (diff <= 0) {
          this.nextDelivery = this._calculateNextDeliveryDay();
          this.style = '';
          this.innerHTML = '';
          clearInterval(this.interval);
          this._calculateRemainingTime();
        }

        this._renderDeliveryTime();

      }, 1000);
    }, now.getMilliseconds());
  }

  _renderDeliveryTime() {
    this.innerHTML = `Order within ${this._timeAgo(this.nextDelivery.nextDispatchDay)}`;
  }
  
  _calculateNextDeliveryDay() {
    const now = new Date();
    let nextDeliveryDay;
    let nextDispatchDay;
    
    const isPastCutoff = now.getHours() >= this.deliveryCutoff;
    
    let daysToCheck = isPastCutoff ? 1 : 0;
    
    do {
      const checkDay = new Date(now);
      checkDay.setDate(now.getDate() + daysToCheck);
      
      const dayOfWeek = checkDay.getDay();
      
      const normalizedDay = dayOfWeek === 0 ? 7 : dayOfWeek;
      
      const isDeliveryDay = this.deliveryDays.some(day => day[0] === normalizedDay);
      
      if (isDeliveryDay) {
        nextDeliveryDay = checkDay;
        
        nextDispatchDay = new Date(checkDay);
        nextDispatchDay.setHours(this.deliveryCutoff - 1, 59, 59, 0);
        
        if (isPastCutoff && daysToCheck === 0) {
          nextDeliveryDay.setDate(nextDeliveryDay.getDate() + 7);
          nextDispatchDay.setDate(nextDispatchDay.getDate() + 7);
        }
        
        break;
      }
      
      daysToCheck++;
    } while (daysToCheck < 14);
    
    return {
      nextDispatchDay,
      nextDeliveryDay
    };
  }

  _timeAgo(cutoff) {
    const diff = (cutoff - Date.now());
    const duration = {
      days: Math.trunc(diff / (1000 * 60 * 60 * 24)),
      hours: Math.trunc((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.trunc((diff % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.trunc((diff % (1000 * 60)) / 1000)
    }

    if(duration.hours === 0 && duration.days == 0) {
      this.style.color = 'darkred';
      this.style.fontWeight = 600;
    }

    return new Intl.DurationFormat("en", {
      style: "long"
    }).format(duration);
  }
}

window.customElements.define('delivery-info', DeliveryInfo);
var ProductDescription = class extends CustomHTMLElement {
  connectedCallback() {
    this.productDescription = document.querySelector(
      ".product-form__description",
    );

    const textContainer = this.productDescription.querySelector('.text-container');
    if (textContainer.childElementCount !== 1) {
      this.productDescription.setAttribute('aria-expanded', false);
      this.delegate.on("click", '.expand-btn', this._expandDescription.bind(this));
    } else {
      this.productDescription.setAttribute('aria-expanded', true);
      this.querySelector('.expand-btn').remove();
    }
  }

  _expandDescription(e) {
    this.productDescription.ariaExpanded = this.productDescription.ariaExpanded !== 'true';
    const btnText = this.querySelector('.text--subdued');
    if (btnText.innerHTML == 'Show more') {
      btnText.innerHTML = 'Show less';

    } else {
      btnText.innerHTML = 'Show more';
    }

    this.productDescription.querySelector('.text-container').classList.toggle('active');

  }

};
window.customElements.define("product-description", ProductDescription);

(function () {
  const settings = (window.themeVariables && window.themeVariables.settings) || {};

  if (settings.currencyCodeEnabled) {
    return;
  }

  const moneyFormat = settings.moneyFormat;

  function isoCode() {
    if (window.Shopify && Shopify.currency && Shopify.currency.active) return Shopify.currency.active;
    if (typeof clearpay_shop_currency !== 'undefined') return clearpay_shop_currency;
    return '';
  }

  function toCents(str) {
    if (str == null) return null;
    let s = String(str);
    const iso = isoCode();
    if (iso) s = s.split(iso).join('');
    s = s.replace(/[^\d.,\-]/g, '');
    const lastDot = s.lastIndexOf('.');
    const lastComma = s.lastIndexOf(',');
    if (lastDot > -1 && lastComma > -1) {
      if (lastComma > lastDot) s = s.replace(/\./g, '').replace(',', '.');
      else s = s.replace(/,/g, '');
    } else if (lastComma > -1) {
      s = s.length - lastComma - 1 === 2 ? s.replace(',', '.') : s.replace(/,/g, '');
    } else if (lastDot > -1) {
      if (s.length - lastDot - 1 !== 2) s = s.replace(/\./g, '');
    }
    const cents = Math.round(parseFloat(s) * 100);
    return isNaN(cents) ? null : cents;
  }

  function format(cents) {
    if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(cents, moneyFormat);
    const dollars = (cents / 100).toFixed(2).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    return String(moneyFormat || '').replace(/\{\{\s*(currency|code)\s*\}\}/g, '').replace(/\{\{\s*amount\s*\}\}/, dollars);
  }

  let lock = false;

  function normalize(el) {
    if (!el) return;
    const cents = toCents(el.textContent);
    if (cents == null) return;
    const next = format(cents);
    if (next == null) return;
    const text = el.innerHTML;
    if (text !== next) {
      lock = true;
      el.innerHTML = next;
      requestAnimationFrame(() => { lock = false; });
    }
  }

  function refresh(scope) {
    if (!scope) return;
    normalize(scope.querySelector('.product-single__price'));
    normalize(scope.querySelector('.product-single__price--compare-at'));
  }

  const productInfo = document.getElementById('shopify-section-product-template');
  if (!productInfo) return;

  refresh(productInfo);

  const observer = new MutationObserver(() => {
    if (lock) return;
    refresh(productInfo);
  });

  observer.observe(productInfo, { subtree: true, childList: true, characterData: true });
})();


(function () {
  const productInfo = document.getElementById('shopify-section-product-template');
  if (!productInfo) return;

  const productPlacement = () =>
    productInfo.querySelector('square-placement[data-page-type="product"]') ||
    productInfo.querySelector('square-placement');

  function sync() {
    const el = productPlacement();
    if (!el) return;

    const planInput = productInfo.querySelector('.selected-selling-plan-id');
    const selectedPlan = planInput && planInput.value ? planInput.value.trim() : '';

    const addButton = productInfo.querySelector('.product-form__add-button[type="submit"]');
    const available = !addButton || !addButton.disabled;

    el.style.display = selectedPlan || !available ? 'none' : '';
  }

  function scheduleSync() { requestAnimationFrame(sync); }

  productInfo.addEventListener('change', (event) => {
    const target = event.target;
    if (target && target.matches && target.matches('.shopify_subscriptions_app_block input[type="radio"], .selected-selling-plan-id')) {
      scheduleSync();
    }
  });

  sync();
})();
